// Builds an AgentObservation from the client's own view of the world. This is deliberately
// sourced from ClientGameLogic (the client's partial replica) rather than server truth, so
// the agent perceives exactly what the human player's client knows — natural fog-of-war.
import { Mechanics } from "game-common/mechanics/mechanics"
import { Entity, TileSize } from "game-common/models"

import { ClientGameLogic } from "../client_game_logic"
import { AgentObservation, LocalTerrain, ObservedEntity } from "./types"

const NEARBY_RADIUS_TILES = 16 // matches the server's detail range (15 tiles)
const MAX_NEARBY = 20
const FEATURE_RADIUS_TILES = 5

// Compass directions as (dx, dy) tile steps. +y is south (screen down).
const DIRS: Record<string, [number, number]> = {
    N: [0, -1],
    S: [0, 1],
    E: [1, 0],
    W: [-1, 0],
    NE: [1, -1],
    NW: [-1, -1],
    SE: [1, 1],
    SW: [-1, 1],
}

// Entity-tile ids the agent should be told about (harvestable trees etc.). Mirrors the
// forager's tree list; unknown entity-tiles are still reported generically by their id.
const NOTABLE_TILE_KINDS = new Set([
    "tree1", "tree2", "tree3", "tree4", "tree5",
    "smallTree1", "palmTree1", "palmTree2", "palmTree3", "palmTree4", "palmTree5", "palmTree6",
])

const round = (n: number, dp = 1) => {
    const f = Math.pow(10, dp)
    return Math.round(n * f) / f
}

function buildTerrain(logic: ClientGameLogic, playerTileX: number, playerTileY: number): LocalTerrain {
    const tmp = logic.tilemapMetaProvider
    // Sparse: list only the BLOCKED directions (open ground is the common case, so emitting
    // all-false entries every tick is pure token waste). Empty array = fully open.
    const blocked1: string[] = []
    const blocked2: string[] = []

    const isBlocked = (tx: number, ty: number): boolean => {
        if (!tmp) return false
        // No approach coords => "blocked no matter what" (pure wall/water test).
        return tmp.isCollisionAt(tx, ty) || (tmp.isDeepAt ? tmp.isDeepAt(tx, ty) : false)
    }

    for (const [dir, [dx, dy]] of Object.entries(DIRS)) {
        if (isBlocked(playerTileX + dx, playerTileY + dy)) blocked1.push(dir)
        if (isBlocked(playerTileX + dx * 2, playerTileY + dy * 2)) blocked2.push(dir)
    }

    const features: LocalTerrain["features"] = []
    if (tmp?.getEntityTileAt) {
        for (let dx = -FEATURE_RADIUS_TILES; dx <= FEATURE_RADIUS_TILES && features.length < 12; dx++) {
            for (let dy = -FEATURE_RADIUS_TILES; dy <= FEATURE_RADIUS_TILES && features.length < 12; dy++) {
                if (dx === 0 && dy === 0) continue
                let hit: { tileId?: string } | undefined
                try {
                    hit = tmp.getEntityTileAt(playerTileX + dx, playerTileY + dy)
                } catch {
                    hit = undefined
                }
                const id = hit?.tileId
                if (id && NOTABLE_TILE_KINDS.has(id)) {
                    features.push({ kind: id, dxTiles: dx, dyTiles: dy })
                }
            }
        }
    }

    return { blocked1, blocked2, features }
}

export function buildObservation(
    logic: ClientGameLogic,
    goal: string,
    tick: number,
    recentChat: Array<{ from: string; message: string }> = [],
): AgentObservation | null {
    const player = logic.playerEntity
    if (!player || !player.location) return null

    const px = player.location.x
    const py = player.location.y
    const playerTileX = Math.floor(px / TileSize)
    const playerTileY = Math.floor(py / TileSize)

    const nearby: ObservedEntity[] = []
    logic.entityManager.iterate(["npc", "player"], (e: Entity) => {
        if (!e || e.entityId === player.entityId || !e.location) return
        if (e.entityState === "dead") return
        const dxPx = e.location.x - px
        const dyPx = e.location.y - py
        const distTiles = Math.sqrt(dxPx * dxPx + dyPx * dyPx) / TileSize
        if (distTiles > NEARBY_RADIUS_TILES) return

        let disposition = "neutral"
        try {
            disposition = Mechanics.social.computeDisposition(e, player) as unknown as string
        } catch {
            disposition = "neutral"
        }

        nearby.push({
            entityId: e.entityId,
            name: e.safeName ? e.safeName() : e.name || e.npcType || e.entityType,
            npcType: e.npcType,
            x: round(e.location.x, 0),
            y: round(e.location.y, 0),
            dxTiles: round(dxPx / TileSize),
            dyTiles: round(dyPx / TileSize),
            distanceTiles: round(distTiles),
            hp: e.hp,
            maxHp: e.maxHp,
            faction: e.faction || undefined,
            disposition,
        })
    })
    nearby.sort((a, b) => a.distanceTiles - b.distanceTiles)
    if (nearby.length > MAX_NEARBY) nearby.length = MAX_NEARBY

    const inventory = (player.inventory?.items || [])
        .filter(i => !!i.itemType)
        .map(i => ({ itemType: i.itemType as string, quantity: i.quantity, readied: i.readied }))

    return {
        goal,
        tick,
        roomName: (logic as any).roomName,
        dayPhase: logic.dayPhase ? (logic.dayPhase as any).phase || undefined : undefined,
        self: {
            entityId: player.entityId,
            x: round(px, 0),
            y: round(py, 0),
            hp: player.hp,
            maxHp: player.maxHp,
            mana: player.mana,
            hunger: player.hunger,
            roomId: player.roomId,
            faction: player.faction || undefined,
            moving: !!player.movement?.moving,
            inventory,
        },
        nearby,
        terrain: buildTerrain(logic, playerTileX, playerTileY),
        recentChat: recentChat.slice(-8),
    }
}
